/**
 * Investor Activity Log — chronological event feed
 *
 * Route: /admin/reporting/investors/:id/activity
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { DatePicker } from '@/common/components/date-picker'
import { useAuth } from '@/common/hooks/useAuth'
import { useInvestorActivity } from '@/features/reporting/hooks/useReportingData'
import { fetchInvestorActivity } from '@/features/reporting/api/reportingApi'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { table } from '@/features/reporting/chartPalette'
import * as styles from '../../index.css'

export const Route = createFileRoute({
  component: InvestorActivityLogPage,
})

// =============================================================================
// Constants
// =============================================================================

type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'

const EVENT_TYPE_CONFIG: Record<string, { label: string; icon: string }> = {
  offer_submitted: { label: 'Offer Submitted', icon: 'gavel' },
  offer_accepted: { label: 'Offer Accepted', icon: 'check_circle' },
  offer_rejected: { label: 'Offer Rejected', icon: 'cancel' },
  offer_withdrawn: { label: 'Offer Withdrawn', icon: 'undo' },
  inquiry_submitted: { label: 'Inquiry Submitted', icon: 'question_answer' },
  email_sent: { label: 'Email Sent', icon: 'mail' },
  email_opened: { label: 'Email Opened', icon: 'mark_email_read' },
  email_clicked: { label: 'Email Clicked', icon: 'ads_click' },
  property_closed: { label: 'Property Closed', icon: 'home' },
  login: { label: 'Login', icon: 'login' },
  agreement_signed: { label: 'Agreement Signed', icon: 'verified' },
}

const ALL_EVENT_TYPES = Object.keys(EVENT_TYPE_CONFIG)

// =============================================================================
// Metadata Renderers
// =============================================================================

function renderMetadata(eventType: string, metadata: Record<string, unknown>) {
  switch (eventType) {
    case 'offer_submitted':
    case 'offer_accepted':
    case 'offer_rejected':
    case 'offer_withdrawn':
      return (
        <Stack gap="4">
          {!!metadata.property_address && (
            <Text variant="bodySm" color="muted">Property: {String(metadata.property_address)}</Text>
          )}
          {metadata.offer_amount != null && (
            <Text variant="bodySm" color="muted">
              Offer Amount: ${Number(metadata.offer_amount).toLocaleString('en-US')}
            </Text>
          )}
        </Stack>
      )

    case 'inquiry_submitted':
      return (
        <Stack gap="4">
          {!!metadata.property_address && (
            <Text variant="bodySm" color="muted">Property: {String(metadata.property_address)}</Text>
          )}
          {!!metadata.message && (
            <Text variant="bodySm" color="muted">Message: {String(metadata.message)}</Text>
          )}
        </Stack>
      )

    case 'email_sent':
    case 'email_opened':
    case 'email_clicked':
      return (
        <Stack gap="4">
          {!!metadata.subject && (
            <Text variant="bodySm" color="muted">Subject: {String(metadata.subject)}</Text>
          )}
          {!!metadata.email_type && (
            <Text variant="bodySm" color="muted">Type: {String(metadata.email_type)}</Text>
          )}
        </Stack>
      )

    case 'property_closed':
      return (
        <Stack gap="4">
          {!!metadata.property_address && (
            <Text variant="bodySm" color="muted">Property: {String(metadata.property_address)}</Text>
          )}
          {metadata.closing_price != null && (
            <Text variant="bodySm" color="muted">
              Closing Price: ${Number(metadata.closing_price).toLocaleString('en-US')}
            </Text>
          )}
        </Stack>
      )

    case 'agreement_signed':
      return metadata.agreement_version ? (
        <Text variant="bodySm" color="muted">Version: {String(metadata.agreement_version)}</Text>
      ) : null

    default:
      return null
  }
}

// =============================================================================
// Main Component
// =============================================================================

function InvestorActivityLogPage() {
  const { id } = Route.useParams()
  const { token } = useAuth()

  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(new Set(ALL_EVENT_TYPES))
  const [dateRange, setDateRange] = useState<DateRangePreset>('1y')
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [showEventTypeFilter, setShowEventTypeFilter] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
    setExpandedRows(new Set())
  }, [selectedEventTypes, dateRange, customStart, customEnd, debouncedSearch])

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 12), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date = subMonths(now, 12)
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  const noTypesSelected = selectedEventTypes.size === 0

  const eventTypesParam = useMemo(() => {
    if (selectedEventTypes.size === 0 || selectedEventTypes.size === ALL_EVENT_TYPES.length) return undefined
    return Array.from(selectedEventTypes).join(',')
  }, [selectedEventTypes])

  const { data: activityRes, isFetching } = useInvestorActivity(id, {
    eventType: eventTypesParam,
    startDate: startDateStr,
    endDate: endDateStr,
    search: debouncedSearch || undefined,
    page,
    pageSize: 50,
  })

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange])

  const toggleEventType = useCallback((eventType: string) => {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev)
      if (next.has(eventType)) { next.delete(eventType) } else { next.add(eventType) }
      return next
    })
  }, [])

  const toggleRow = useCallback((index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(index)) { next.delete(index) } else { next.add(index) }
      return next
    })
  }, [])

  const clearAllEventTypes = useCallback(() => setSelectedEventTypes(new Set()), [])
  const selectAllEventTypes = useCallback(() => setSelectedEventTypes(new Set(ALL_EVENT_TYPES)), [])

  return (
    <>
      {/* Controls */}
      <div className={styles.controls}>
        {/* Event Type Filter */}
        <div style={{ position: 'relative' }}>
          <Button variant="outline" size="sm" onClick={() => setShowEventTypeFilter((v) => !v)}>
            <Stack direction="row" gap="4" align="center">
              <Icon name="filter_list" size={16} />
              <span>Event Types{selectedEventTypes.size > 0 && selectedEventTypes.size < ALL_EVENT_TYPES.length && ` (${selectedEventTypes.size})`}</span>
            </Stack>
          </Button>

          {showEventTypeFilter && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 10,
              backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
              padding: '12px', marginTop: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: '220px',
            }}>
              <Stack gap="8">
                <Stack direction="row" gap="8">
                  <button onClick={selectAllEventTypes} style={{ border: 'none', background: 'none', color: '#027F87', cursor: 'pointer', fontSize: '12px', padding: 0 }}>Select All</button>
                  <button onClick={clearAllEventTypes} style={{ border: 'none', background: 'none', color: '#027F87', cursor: 'pointer', fontSize: '12px', padding: 0 }}>Clear All</button>
                </Stack>
                {ALL_EVENT_TYPES.map((type) => {
                  const config = EVENT_TYPE_CONFIG[type]
                  return (
                    <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      <input type="checkbox" checked={selectedEventTypes.has(type)} onChange={() => toggleEventType(type)} />
                      <Icon name={config.icon} size={16} />
                      <span>{config.label}</span>
                    </label>
                  )
                })}
              </Stack>
            </div>
          )}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', width: '280px' }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              width: '100%', height: '36px', paddingLeft: '34px', paddingRight: '12px',
              border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px',
              fontFamily: 'SharpGrotesk, sans-serif', outline: 'none',
            }}
          />
        </div>

        {/* Date Range */}
        <div className={styles.controlDateGroup}>
          <div className={styles.granularityToggle}>
            {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
              <button
                key={preset}
                className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)}
                onClick={() => handleDateRangeChange(preset)}
              >
                {preset === 'custom' ? 'Custom' : preset}
              </button>
            ))}
          </div>
          <div className={styles.customDateRange}>
            <div className={styles.customDatePicker}>
              <DatePicker label="Start" value={new Date(startDateStr)} onChange={setCustomStart} maxDate={new Date(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
            </div>
            <Text variant="bodySm" color="muted">{'\u2013'}</Text>
            <div className={styles.customDatePicker}>
              <DatePicker label="End" value={new Date(endDateStr)} onChange={setCustomEnd} minDate={new Date(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
            </div>
          </div>
        </div>
      </div>

      {/* Activity Log Table */}
      <div style={{ position: 'relative', minHeight: '400px' }}>
        {isFetching && (
          <div style={{
            position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)',
            zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
          }}>
            <LoadingSpinner size="sm" />
          </div>
        )}

        {noTypesSelected && !isFetching && (
          <Stack gap="16" align="center" justify="center" style={{ padding: '80px 24px' }}>
            <Icon name="filter_list" size={36} style={{ color: '#999' }} />
            <Text variant="body" color="muted">Select event types to view activity</Text>
          </Stack>
        )}

        {!noTypesSelected && activityRes?.data && activityRes.data.length === 0 && !isFetching && (
          <Stack gap="16" align="center" justify="center" style={{ padding: '80px 24px' }}>
            <Icon name="event_note" size={36} style={{ color: '#999' }} />
            <Text variant="body" color="muted">No activity found</Text>
          </Stack>
        )}

        {!noTypesSelected && activityRes?.data && activityRes.data.length > 0 && (
          <>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                {activityRes.pagination ? `${activityRes.pagination.total_count} events` : ''}
              </Text>
              <ExportMenu options={[
                {
                  label: 'Export Page (CSV)',
                  data: activityRes.data.map((e) => ({
                    event_type: EVENT_TYPE_CONFIG[e.event_type]?.label ?? e.event_type,
                    description: e.description,
                    timestamp: e.timestamp,
                    property_address: e.property_address,
                    market: e.market,
                    status: e.status,
                    amount: e.amount,
                  })) as unknown as Record<string, unknown>[],
                  filename: `investor-activity-${id}-page${page}.csv`,
                },
                ...(activityRes.pagination && activityRes.pagination.total_pages > 1 ? [{
                  label: 'Export All (CSV)',
                  description: `${activityRes.pagination.total_count} events`,
                  fetchData: async (): Promise<Record<string, unknown>[]> => {
                    if (!token) return []
                    const allData: Record<string, unknown>[] = []
                    const totalPages = activityRes.pagination!.total_pages
                    for (let p = 1; p <= totalPages; p++) {
                      const res = await fetchInvestorActivity(token, {
                        investor_id: id,
                        event_type: eventTypesParam,
                        start_date: startDateStr,
                        end_date: endDateStr,
                        search: debouncedSearch || undefined,
                        page: p,
                        page_size: 50,
                      })
                      for (const e of res.data) {
                        allData.push({
                          event_type: EVENT_TYPE_CONFIG[e.event_type]?.label ?? e.event_type,
                          description: e.description,
                          timestamp: e.timestamp,
                          property_address: e.property_address,
                          market: e.market,
                          status: e.status,
                          amount: e.amount,
                        })
                      }
                    }
                    return allData
                  },
                  filename: `investor-activity-${id}-all.csv`,
                }] : []),
              ]} />
            </div>
            <table className={styles.recordsTable}>
              <thead>
                <tr>
                  <th style={{ width: '40px', padding: '8px', borderBottom: `1px solid ${table.headerBorder}` }} />
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Event Type</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Description</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px', whiteSpace: 'nowrap' }}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {activityRes.data.map((event, idx) => {
                  const config = EVENT_TYPE_CONFIG[event.event_type] ?? { label: event.event_type, icon: 'info' }
                  const isExpanded = expandedRows.has(idx)
                  const metadata: Record<string, unknown> = {
                    ...(event.property_address ? { property_address: event.property_address } : {}),
                    ...(event.market ? { market: event.market } : {}),
                    ...(event.status ? { status: event.status } : {}),
                    ...(event.amount != null ? { offer_amount: event.amount } : {}),
                  }
                  const hasMetadata = Object.keys(metadata).length > 0

                  return (
                    <tr key={idx} style={{ cursor: hasMetadata ? 'pointer' : undefined }} onClick={() => hasMetadata && toggleRow(idx)}>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, verticalAlign: 'top' }}>
                        {hasMetadata && <Icon name={isExpanded ? 'expand_less' : 'expand_more'} size={16} style={{ color: '#999' }} />}
                      </td>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, verticalAlign: 'top' }}>
                        <Stack direction="row" gap="8" align="center">
                          <Icon name={config.icon} size={16} style={{ color: '#31324C', flexShrink: 0 }} />
                          <Text variant="bodySm">{config.label}</Text>
                        </Stack>
                      </td>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, verticalAlign: 'top' }}>
                        <Stack gap="4">
                          <Text variant="bodySm">{event.description}</Text>
                          {isExpanded && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#f8f8f8', borderRadius: '4px' }}>
                              {renderMetadata(event.event_type, metadata)}
                            </div>
                          )}
                        </Stack>
                      </td>
                      <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, verticalAlign: 'top', whiteSpace: 'nowrap', color: table.headerText }}>
                        {event.timestamp
                          ? new Date(event.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                          : '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {activityRes.pagination && (
              <div className={styles.paginationControls}>
                <Text variant="bodyXs" color="muted">
                  Page {activityRes.pagination.page} of {activityRes.pagination.total_pages}
                  {' '}({activityRes.pagination.total_count} events)
                </Text>
                <Stack direction="row" gap="8">
                  <Button variant="outline" size="sm" disabled={isFetching || activityRes.pagination.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={isFetching || !activityRes.pagination.has_more} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </Stack>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
